import { Background, Controls, type Edge, type Node, ReactFlow, type ReactFlowInstance } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import type { DipPeekTarget } from "~/features/dataImportProjects/types";
import { DipGroupNode } from "./nodes/DipGroupNode";
import { DipItemNode } from "./nodes/DipItemNode";
import { DipSchemaNode } from "./nodes/DipSchemaNode";
import { DipStageHeaderNode } from "./nodes/DipStageHeaderNode";

const nodeTypes = {
  dipGroup: DipGroupNode,
  dipItem: DipItemNode,
  dipStageHeader: DipStageHeaderNode,
  dipSchema: DipSchemaNode,
} as const;

const FIT_VIEW_PADDING = 0.04;

interface DipPipelineFlowProps {
  nodes: Node[];
  edges: Edge[];
  /** Canvas height utility class, e.g. "h-[460px]". */
  heightClass: string;
  onNodeOpen?: (peek: DipPeekTarget) => void;
}

function isDipPeekTarget(value: unknown): value is DipPeekTarget {
  if (value == null || typeof value !== "object") return false;
  const maybePeek = value as Partial<DipPeekTarget>;
  return typeof maybePeek.type === "string" && typeof maybePeek.id === "string";
}

export function DipPipelineFlow({ nodes, edges, heightClass, onNodeOpen }: DipPipelineFlowProps) {
  const navigate = useNavigate();
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance<Node, Edge> | null>(null);

  useEffect(() => {
    if (!reactFlowInstance || nodes.length === 0) return;
    void reactFlowInstance.fitView({ padding: FIT_VIEW_PADDING });
  }, [nodes, reactFlowInstance]);

  return (
    <div
      className={`${heightClass} overflow-hidden rounded-[var(--radius-md)] border border-border-default bg-bg-page`}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={(_, node) => {
          const peek = node.data?.peek;
          if (isDipPeekTarget(peek) && onNodeOpen) {
            onNodeOpen(peek);
            return;
          }
          const detailPath = node.data?.detailPath;
          if (typeof detailPath === "string" && detailPath.length > 0) {
            navigate(detailPath);
          }
        }}
        onInit={setReactFlowInstance}
        fitView
        fitViewOptions={{ padding: FIT_VIEW_PADDING }}
        minZoom={0.2}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        panOnScroll={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} color="#E5E7EB" />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
    </div>
  );
}
